import { Mainnet } from "@nibiruchain/nibijs";
import { useDispatch, useSelector } from "react-redux";
import { NUSDIcon, WhiteCheckMarkIcon } from "../../../AssetComponents/Images";
import { PurpleButton } from "../../../Components/Buttons/PurpleButton";
import { RedButton } from "../../../Components/Buttons/RedButton";
import { BlackButton } from "../../../Components/Buttons/BlackButton";
import { useLocation, useNavigate } from "react-router-dom";
import { updateToken } from "../../../Components/functions/Functions";
import { useEffect, useMemo } from "react";
import { api } from "../../../Components/functions/Api";
import { updateNFTStore } from "../../../ReduxSlices/NFTSlice";
import { toast } from "react-toastify";
import { executeRentalWithWallet } from "../../../Components/functions/Contract";
import { evmToBech32Address } from "../../../evm/services/EvmContract";

export const HostAction = ({
  token_id,
  traveler,
  cancelled,
  fundedAmount,
  rentingPeriod,
  approved,
  reviewPlaced = false,
  setViewDetailFlag = () => {},
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const account = useSelector((state) => state.auth.account);
  const currentTime = new Date();
  const diff = useSelector((state) => state.time.diffToUTC);
  const token = useSelector((state) => state.nft.nfts[token_id]);
  const wallet = useSelector((state) => state.auth.wallet);
  const mainnet = Mainnet();

  const fundedAmountSafe = useMemo(() => {
    const n =
      typeof fundedAmount === "string" ? Number(fundedAmount) : fundedAmount;
    if (!Number.isFinite(n)) return 0;
    return n;
  }, [fundedAmount]);

  const getContractTravelerAddress = async (address) => {
    if (address && address.startsWith("0x")) {
      try {
        return await evmToBech32Address(address);
      } catch (e) {
        console.error("Failed to convert traveler address:", e);
        return address;
      }
    }
    return address;
  };

  // 🚀 INSTANT DASHBOARDS: Use backend API instead of RPC calls
  const getToken = async () => {
    if (!token && token_id) {
      try {
        // Use backend API instead of RPC (faster, no blockchain query)
        const propertyData = await api("property/getProperty", {
          token_id: token_id,
        });
        if (propertyData) {
          // Update Redux with backend data (no RPC needed)
          dispatch(
            updateNFTStore({
              token_id: token_id,
              metaData: propertyData.metaData,
              short: propertyData.short,
              rentals: propertyData.rentals,
              access: propertyData.access,
            })
          );
        }
      } catch (error) {
        console.error("Failed to get property from backend:", error);
        // Only fallback to RPC if backend fails
        const mainnet = Mainnet();
        await updateToken(
          {
            token_id: token_id,
            contract: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
          },
          mainnet.endptTm,
          dispatch
        );
      }
    }
  };

  // 🚀 OPTIMIZED: Only fetch if token is missing, prevent unnecessary re-renders
  useEffect(() => {
    if (!token && token_id) {
      getToken();
    }
  }, [token_id]); // Only depend on token_id, not token (prevents loops)

  const navigate = useNavigate();
  
  // 🚀 PHASE 6: Approve reservation - state changes only after on-chain success
  const approveReservation = async () => {
    try {
      const contractTraveler = await getContractTravelerAddress(traveler);

      // Optional pre-check: validate on backend that reservation can be approved
      const preCheck = await api("property/approveReservation", {
        token_id: token_id,
        traveler: traveler,
        renting_period: [rentingPeriod[0], rentingPeriod[1]],
        account: account,
      });

      if (!preCheck?.success) {
        toast.error(preCheck?.error || "Failed to approve reservation");
        return;
      }

      // Step 1: Execute blockchain transaction (Nibiru or EVM)
      const message = {
        set_approve_for_short_term: {
          token_id: token_id,
          traveler: contractTraveler,
          renting_period: [
            rentingPeriod[0].toString(),
            rentingPeriod[1].toString(),
          ],
        },
      };

      const txHash = await executeRentalWithWallet({
        emailData: {
          date:
            new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "numeric",
            }).format(new Date(rentingPeriod[0] * 1000)) +
            "~" +
            new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "numeric",
            }).format(new Date(rentingPeriod[1] * 1000)),
          checkin: token?.metaData.checkIn,
          checkout: token?.metaData.checkOut,
          image: token?.metaData.images[0],
          buildingName: token?.metaData.buildingName,
          price: Math.round(
            Math.round((rentingPeriod[1] - rentingPeriod[0]) / 86400) *
              token?.short.price_per_day
          ).toString(),
          detail: `https://test.codedestate.com/dashboard/traveler/trips/${token_id}?from=${rentingPeriod[0].toString()}&to=${rentingPeriod[1].toString()}`,
          approved: token?.short?.auto_approve,
        },
        funds: null,
        currentTime,
        chainId: mainnet.chainId,
        endpt: mainnet.endptTm,
        contractAddr: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
        dispatch,
        tokenId: token_id,
        receiver: contractTraveler,
        CONTRACT_MESSAGE: message,
        account,
        walletType: wallet,
        walletEx: wallet,
        tokenToSend: null,
      });

      // 🚀 FIX: Handle case where reservation is already approved on-chain (Nibiru path)
      if (txHash && typeof txHash === 'object' && txHash.alreadyApproved) {
        console.log("🔄 [FRONTEND] Reservation already approved on-chain, syncing state...");
        console.log("🔄 [FRONTEND] Sync request data:", {
          token_id,
          traveler,
          renting_period: [rentingPeriod[0], rentingPeriod[1]],
          account,
        });
        
        // Sync the approval state with backend
        try {
          console.log("📡 [FRONTEND] Calling syncReservationApproval API...");
          const syncResponse = await api("property/syncReservationApproval", {
            token_id: token_id,
            traveler: traveler,
            renting_period: [rentingPeriod[0], rentingPeriod[1]],
            account: account,
          });

          console.log("📥 [FRONTEND] Sync response received:", syncResponse);

          if (syncResponse?.success) {
            console.log("✅ [FRONTEND] Sync successful, refreshing property data...");
            // Refresh property in Redux FIRST before showing success message
            try {
              console.log(`🔍 [FRONTEND] Fetching property data for token_id: ${token_id}`);
              const propertyData = await api("property/getProperty", {
                token_id: token_id,
              });
              console.log("📥 [FRONTEND] Property data received:", {
                hasRentals: !!propertyData?.rentals,
                rentalsCount: propertyData?.rentals?.rentals?.length || 0,
                reservationApproved: propertyData?.rentals?.rentals?.find(r => 
                  r.renting_period?.[0] === rentingPeriod[0] && 
                  r.renting_period?.[1] === rentingPeriod[1]
                )?.approved,
              });
              
              if (propertyData) {
                // 🚀 FIX: Create a deep copy to ensure Redux detects the change
                const updatedProperty = {
                  metaData: { ...propertyData.metaData },
                  short: { ...propertyData.short },
                  rentals: {
                    ...propertyData.rentals,
                    rentals: propertyData.rentals?.rentals ? [...propertyData.rentals.rentals] : []
                  },
                  access: { ...propertyData.access },
                };
                
                console.log("💾 [FRONTEND] Updating Redux store...");
                dispatch(
                  updateNFTStore({
                    token_id: token_id,
                    ...updatedProperty,
                  })
                );
                console.log("✅ [FRONTEND] Redux store updated");
                
                // 🚀 FIX: Emit custom event to force UI update (multiple events for compatibility)
                const eventDetail = {
                  token_id: token_id,
                  traveler: traveler,
                  host: account,
                  renting_period: [rentingPeriod[0], rentingPeriod[1]],
                  sync_status: "confirmed",
                  synced: true,
                  approvedAt: new Date().toISOString(),
                };
                
                console.log("📡 [FRONTEND] Emitting events:", {
                  'reservation:approved': eventDetail,
                  'reservationStatusUpdate': { ...eventDetail, status: 'approved' },
                  'reservationApproved': eventDetail,
                });
                
                // Emit multiple events to ensure all listeners catch it
                window.dispatchEvent(new CustomEvent('reservation:approved', { detail: eventDetail }));
                window.dispatchEvent(new CustomEvent('reservationStatusUpdate', { detail: { ...eventDetail, status: 'approved' } }));
                window.dispatchEvent(new CustomEvent('reservationApproved', { detail: eventDetail }));
                
                console.log("⏳ [FRONTEND] Waiting 800ms for Redux and event propagation...");
                // Wait for backend to process and Redux to update
                await new Promise(resolve => setTimeout(resolve, 800));
                
                console.log("📡 [FRONTEND] Emitting force refresh events...");
                // Force multiple refresh events to ensure all components update
                window.dispatchEvent(new Event('reservation:forceRefresh'));
                window.dispatchEvent(new CustomEvent('reservation:refreshList'));
                
                // Also emit the approved event again to ensure listeners catch it
                window.dispatchEvent(new CustomEvent('reservation:approved', { detail: eventDetail }));
                window.dispatchEvent(new CustomEvent('reservationStatusUpdate', { detail: { ...eventDetail, status: 'approved' } }));
                console.log("✅ [FRONTEND] All events emitted");
              }
            } catch (error) {
              console.error("❌ [FRONTEND] Failed to refresh property after sync:", error);
              console.error("❌ [FRONTEND] Error details:", error.message, error.stack);
            }

            toast.success("Reservation is already approved. State synced successfully!", {
              autoClose: 3000,
            });
            console.log("✅ [FRONTEND] Success toast shown");

            // Don't navigate away - let user see the updated status
            // navigate(-1);
          } else {
            console.error("❌ [FRONTEND] Sync failed:", syncResponse?.error);
            toast.error(syncResponse?.error || "Failed to sync reservation state");
          }
        } catch (error) {
          console.error("❌ [FRONTEND] Failed to sync reservation approval:", error);
          console.error("❌ [FRONTEND] Error details:", error.message, error.stack);
          toast.error("Failed to sync reservation state. Please refresh the page.");
        }
        return;
      }

      if (typeof txHash === "string" && txHash.length > 0) {
        // Step 2: Confirm transaction with backend (this will mutate DB + emit socket events)
        await api("property/confirmReservationAction", {
          token_id: token_id,
          traveler: traveler,
          renting_period: [rentingPeriod[0], rentingPeriod[1]],
          txHash: txHash,
          action: "set_approve_for_short_term",
          account: account,
        });

        // 🚀 PHASE 6: Refresh property in Redux immediately for instant UI update
        try {
          const propertyData = await api("property/getProperty", {
            token_id: token_id,
          });
          if (propertyData) {
            // 🚀 FIX: Ensure new array reference for rentals to trigger Redux update
            const updatedProperty = {
              token_id: token_id,
              metaData: propertyData.metaData,
              short: propertyData.short,
              rentals: propertyData.rentals ? {
                ...propertyData.rentals,
                rentals: propertyData.rentals.rentals ? [...propertyData.rentals.rentals] : []
              } : propertyData.rentals,
              access: propertyData.access,
            };
            dispatch(updateNFTStore(updatedProperty));
            
            // Emit event to force UI refresh
            window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
              detail: { token_id, action: 'approved' }
            }));
          }
        } catch (error) {
          console.warn("Failed to refresh property after approval:", error);
        }

        // Optimistic: immediately remove/update from local state
        window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
          detail: { token_id, action: 'approved', immediate: true }
        }));
        // Give backend 800ms to settle then refresh again  
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
            detail: { token_id, action: 'approved', settle: true }
          }));
        }, 800);

        navigate(-1);
      }
    } catch (error) {
      console.error("Approve reservation error:", error);
      toast.error("Failed to approve reservation");
    }
  };
  // 🚀 PHASE 6: Finalize reservation - state changes only after on-chain success
  const finalizeRental = async () => {
    try {
      const contractTraveler = await getContractTravelerAddress(traveler);

      // Optional pre-check
      const preCheck = await api("property/finalizeReservation", {
        token_id: token_id,
        traveler: traveler,
        renting_period: [rentingPeriod[0], rentingPeriod[1]],
        account: account,
      });

      if (!preCheck?.success) {
        toast.error(preCheck?.error || "Failed to finalize reservation");
        return;
      }

      // Step 1: Execute blockchain transaction
      const message = {
        finalize_short_term_rental: {
          token_id: token_id,
          traveler: contractTraveler,
          renting_period: [
            rentingPeriod[0].toString(),
            rentingPeriod[1].toString(),
          ],
        },
      };

      const funds = {
        type: 0,
        amount:
          new Date((rentingPeriod[1] - diff) * 1000) < new Date() || cancelled
            ? approved
              ? cancelled
                ? fundedAmountSafe
                : fundedAmountSafe
              : 0
            : 0,
      };

      const txHash = await executeRentalWithWallet({
        emailData: {},
        funds,
        currentTime,
        chainId: mainnet.chainId,
        endpt: mainnet.endptTm,
        contractAddr: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
        dispatch,
        tokenId: token_id,
        receiver: contractTraveler,
        CONTRACT_MESSAGE: message,
        account,
        walletType: wallet,
        walletEx: wallet,
        tokenToSend: null,
      });

      if (typeof txHash === "string" && txHash.length > 0) {
        // Step 2: Confirm transaction with backend
        await api("property/confirmReservationAction", {
          token_id: token_id,
          traveler: traveler,
          renting_period: [rentingPeriod[0], rentingPeriod[1]],
          txHash: txHash,
          action: "finalize_short_term_rental",
          account: account,
        });

        // 🚀 PHASE 6: Refresh property in Redux immediately for instant UI update
        try {
          const propertyData = await api("property/getProperty", {
            token_id: token_id,
          });
          if (propertyData) {
            // 🚀 FIX: Ensure new array reference for rentals to trigger Redux update
            const updatedProperty = {
              token_id: token_id,
              metaData: propertyData.metaData,
              short: propertyData.short,
              rentals: propertyData.rentals ? {
                ...propertyData.rentals,
                rentals: propertyData.rentals.rentals ? [...propertyData.rentals.rentals] : []
              } : propertyData.rentals,
              access: propertyData.access,
            };
            dispatch(updateNFTStore(updatedProperty));
            
            // Emit event to force UI refresh
            window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
              detail: { token_id, action: 'finalized' }
            }));
          }
        } catch (error) {
          console.warn("Failed to refresh property after finalization:", error);
        }

        // Optimistic: immediately remove/update from local state
        window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
          detail: { token_id, action: 'finalized', immediate: true }
        }));
        // Give backend 800ms to settle then refresh again  
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
            detail: { token_id, action: 'finalized', settle: true }
          }));
        }, 800);

        navigate(-1);
      }
    } catch (error) {
      console.error("Finalize reservation error:", error);
      toast.error("Failed to finalize reservation");
    }
  };
  // 🚀 PHASE 6: Reject reservation - state changes only after on-chain success
  const rejectReservation = async () => {
    try {
      const contractTraveler = await getContractTravelerAddress(traveler);

      // Optional pre-check
      const preCheck = await api("property/rejectReservation", {
        token_id: token_id,
        traveler: traveler,
        renting_period: [rentingPeriod[0], rentingPeriod[1]],
        account: account,
      });

      if (!preCheck?.success) {
        toast.error(preCheck?.error || "Failed to reject reservation");
        return;
      }

      // Step 1: Execute blockchain transaction
      const message = {
        reject_reservation_for_shortterm: {
          token_id: token_id,
          traveler: contractTraveler,
          renting_period: [
            rentingPeriod[0].toString(),
            rentingPeriod[1].toString(),
          ],
        },
      };

      const txHash = await executeRentalWithWallet({
        emailData: {
          date:
            new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "numeric",
            }).format(new Date(rentingPeriod[0] * 1000)) +
            "~" +
            new Intl.DateTimeFormat("en-US", {
              month: "short",
              day: "numeric",
            }).format(new Date(rentingPeriod[1] * 1000)),
          checkin: token?.metaData.checkIn,
          checkout: token?.metaData.checkOut,
          image: token?.metaData.images[0],
          buildingName: token?.metaData.buildingName,
          price: Math.round(
            Math.round((rentingPeriod[1] - rentingPeriod[0]) / 86400) *
              token?.short.price_per_day
          ).toString(),
          detail: `https://test.codedestate.com/dashboard/traveler/trips/${token_id}?from=${rentingPeriod[0].toString()}&to=${rentingPeriod[1].toString()}`,
          approved: token?.short?.auto_approve,
        },
        funds: null,
        currentTime,
        chainId: mainnet.chainId,
        endpt: mainnet.endptTm,
        contractAddr: process.env.REACT_APP_RENTAL_SMART_CONTRACT,
        dispatch,
        tokenId: token_id,
        receiver: contractTraveler,
        CONTRACT_MESSAGE: message,
        account,
        walletType: wallet,
        walletEx: wallet,
        tokenToSend: null,
      });

      if (typeof txHash === "string" && txHash.length > 0) {
        // Step 2: Confirm transaction with backend
        await api("property/confirmReservationAction", {
          token_id: token_id,
          traveler: traveler,
          renting_period: [rentingPeriod[0], rentingPeriod[1]],
          txHash: txHash,
          action: "reject_reservation_for_shortterm",
          account: account,
        });

        // 🚀 PHASE 6: Refresh property in Redux immediately for instant UI update
        try {
          const propertyData = await api("property/getProperty", {
            token_id: token_id,
          });
          if (propertyData) {
            // 🚀 FIX: Ensure new array reference for rentals to trigger Redux update
            const updatedProperty = {
              token_id: token_id,
              metaData: propertyData.metaData,
              short: propertyData.short,
              rentals: propertyData.rentals ? {
                ...propertyData.rentals,
                rentals: propertyData.rentals.rentals ? [...propertyData.rentals.rentals] : []
              } : propertyData.rentals,
              access: propertyData.access,
            };
            dispatch(updateNFTStore(updatedProperty));
            
            // Emit event to force UI refresh
            window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
              detail: { token_id, action: 'rejected' }
            }));
          }
        } catch (error) {
          console.warn("Failed to refresh property after rejection:", error);
        }

        // Optimistic: immediately remove/update from local state
        window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
          detail: { token_id, action: 'rejected', immediate: true }
        }));
        // Give backend 800ms to settle then refresh again  
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('reservation:forceRefresh', {
            detail: { token_id, action: 'rejected', settle: true }
          }));
        }, 800);

        navigate(-1);
      }
    } catch (error) {
      console.error("Reject reservation error:", error);
      toast.error("Failed to reject reservation");
    }
  };

  // const setReviewMode = () => {
  //   const searchParams = new URLSearchParams(location.search);
  //   searchParams.set("mode", "review");
  //   navigate(location.pathname + `?${searchParams.toString()}`);
  // };

  const setReviewMode = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("mode", "review");
    searchParams.set("from", rentingPeriod[0]);
    searchParams.set("to", rentingPeriod[1]);
    navigate(
      `/dashboard/host/reservations/${token_id}` + `?${searchParams.toString()}`
    );
  };

  return (
    <div className="space-y-2">
      {approved &&
        !cancelled &&
        new Date((rentingPeriod[1] - diff) * 1000) > currentTime && (
          <div className="flex gap-[10px] items-center">
            <div className="p-[2px] bg-[#38A569] rounded-full shadow-md border-[3px] border-[#38A56966]">
              <WhiteCheckMarkIcon className="w-[12px]" />
            </div>
            <div className="text-[18px] font-semibold text-[#38A569]">
              Approved
            </div>
          </div>
        )}
      {approved &&
        !cancelled &&
        new Date((rentingPeriod[1] - diff) * 1000) < currentTime && (
          <div className="flex gap-[10px] items-center">
            <div className="p-[2px] bg-[#38A569] rounded-full shadow-md border-[3px] border-[#38A56966]">
              <WhiteCheckMarkIcon className="w-[12px]" />
            </div>
            <div className="text-[18px] font-semibold text-[#38A569]">
              Completed
            </div>
          </div>
        )}

      {cancelled && (
        <div className="flex items-center">
          <svg
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g filter="url(#filter0_dd_959_57956)">
              <rect x="8" y="8" width="18" height="18" rx="9" fill="#DB1F22" />
              <rect
                x="8"
                y="8"
                width="18"
                height="18"
                rx="9"
                fill="url(#paint0_linear_959_57956)"
                fill-opacity="0.18"
              />
              <rect
                x="8.33333"
                y="8.33333"
                width="17.3333"
                height="17.3333"
                rx="8.66667"
                stroke="white"
                stroke-opacity="0.2"
                stroke-width="0.666667"
              />
              <path
                d="M15.125 15.125L18.875 18.875"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M15.125 18.875L18.875 15.125"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </g>
            <defs>
              <filter
                id="filter0_dd_959_57956"
                x="-9.53674e-07"
                y="-9.53674e-07"
                width="34"
                height="34"
                filterUnits="userSpaceOnUse"
                color-interpolation-filters="sRGB"
              >
                <feFlood flood-opacity="0" result="BackgroundImageFix" />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dx="2" dy="2" />
                <feGaussianBlur stdDeviation="3" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                />
                <feBlend
                  mode="normal"
                  in2="BackgroundImageFix"
                  result="effect1_dropShadow_959_57956"
                />
                <feColorMatrix
                  in="SourceAlpha"
                  type="matrix"
                  values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                  result="hardAlpha"
                />
                <feOffset dx="-2" dy="-2" />
                <feGaussianBlur stdDeviation="3" />
                <feColorMatrix
                  type="matrix"
                  values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                />
                <feBlend
                  mode="normal"
                  in2="effect1_dropShadow_959_57956"
                  result="effect2_dropShadow_959_57956"
                />
                <feBlend
                  mode="normal"
                  in="SourceGraphic"
                  in2="effect2_dropShadow_959_57956"
                  result="shape"
                />
              </filter>
              <linearGradient
                id="paint0_linear_959_57956"
                x1="17"
                y1="8"
                x2="17"
                y2="19.625"
                gradientUnits="userSpaceOnUse"
              >
                <stop stop-color="white" />
                <stop offset="1" stop-color="white" stop-opacity="0" />
              </linearGradient>
            </defs>
          </svg>

          <div className="text-[18px] font-semibold text-[#EB4245]">
            Canceled
          </div>
        </div>
      )}

      {!approved &&
        new Date((rentingPeriod[0] - diff) * 1000) > currentTime && (
          <div className="flex items-center">
            <svg
              width="34"
              height="34"
              viewBox="0 0 34 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_dd_959_57987)">
                <rect
                  x="8"
                  y="8"
                  width="18"
                  height="18"
                  rx="9"
                  fill="#F8BC30"
                />
                <rect
                  x="8"
                  y="8"
                  width="18"
                  height="18"
                  rx="9"
                  fill="url(#paint0_linear_959_57987)"
                  fill-opacity="0.18"
                />
                <rect
                  x="8.33333"
                  y="8.33333"
                  width="17.3333"
                  height="17.3333"
                  rx="8.66667"
                  stroke="white"
                  stroke-opacity="0.2"
                  stroke-width="0.666667"
                />
                <path
                  d="M17.0013 13.25V14.75"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M17.0013 19.25V20.75"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M14.3516 14.35L15.4099 15.4083"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M18.5898 18.5917L19.6482 19.65"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M13.25 17H14.75"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M19.25 17H20.75"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M14.3516 19.65L15.4099 18.5917"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M18.5898 15.4083L19.6482 14.35"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_959_57987"
                  x="-9.53674e-07"
                  y="-9.53674e-07"
                  width="34"
                  height="34"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="2" dy="2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_959_57987"
                  />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="-2" dy="-2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="effect1_dropShadow_959_57987"
                    result="effect2_dropShadow_959_57987"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect2_dropShadow_959_57987"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_959_57987"
                  x1="17"
                  y1="8"
                  x2="17"
                  y2="19.625"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="white" />
                  <stop offset="1" stop-color="white" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>

            <div className="text-[18px] font-semibold text-[#F1B71C]">
              Waiting for approval
            </div>
          </div>
        )}
      {!approved &&
        new Date((rentingPeriod[0] - diff) * 1000) < currentTime && (
          <div className="flex items-center">
            <svg
              width="34"
              height="34"
              viewBox="0 0 34 34"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g filter="url(#filter0_dd_959_57956)">
                <rect
                  x="8"
                  y="8"
                  width="18"
                  height="18"
                  rx="9"
                  fill="#DB1F22"
                />
                <rect
                  x="8"
                  y="8"
                  width="18"
                  height="18"
                  rx="9"
                  fill="url(#paint0_linear_959_57956)"
                  fill-opacity="0.18"
                />
                <rect
                  x="8.33333"
                  y="8.33333"
                  width="17.3333"
                  height="17.3333"
                  rx="8.66667"
                  stroke="white"
                  stroke-opacity="0.2"
                  stroke-width="0.666667"
                />
                <path
                  d="M15.125 15.125L18.875 18.875"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M15.125 18.875L18.875 15.125"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <filter
                  id="filter0_dd_959_57956"
                  x="-9.53674e-07"
                  y="-9.53674e-07"
                  width="34"
                  height="34"
                  filterUnits="userSpaceOnUse"
                  color-interpolation-filters="sRGB"
                >
                  <feFlood flood-opacity="0" result="BackgroundImageFix" />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="2" dy="2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.733333 0 0 0 0 0.764706 0 0 0 0 0.807843 0 0 0 0.6 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="BackgroundImageFix"
                    result="effect1_dropShadow_959_57956"
                  />
                  <feColorMatrix
                    in="SourceAlpha"
                    type="matrix"
                    values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
                    result="hardAlpha"
                  />
                  <feOffset dx="-2" dy="-2" />
                  <feGaussianBlur stdDeviation="3" />
                  <feColorMatrix
                    type="matrix"
                    values="0 0 0 0 0.992157 0 0 0 0 1 0 0 0 0 1 0 0 0 0.8 0"
                  />
                  <feBlend
                    mode="normal"
                    in2="effect1_dropShadow_959_57956"
                    result="effect2_dropShadow_959_57956"
                  />
                  <feBlend
                    mode="normal"
                    in="SourceGraphic"
                    in2="effect2_dropShadow_959_57956"
                    result="shape"
                  />
                </filter>
                <linearGradient
                  id="paint0_linear_959_57956"
                  x1="17"
                  y1="8"
                  x2="17"
                  y2="19.625"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stop-color="white" />
                  <stop offset="1" stop-color="white" stop-opacity="0" />
                </linearGradient>
              </defs>
            </svg>

            <div className="text-[18px] font-semibold text-[#EB4245]">
              Expired
            </div>
          </div>
        )}

      <div className="w-full bg-[#F6F6F6] rounded-[16px] p-[8px] space-y-[8px] mb-2">
        <div className="flex justify-between items-center">
          <div>Period</div>
          <div className="">
            {rentingPeriod?.length &&
              rentingPeriod[0] !== "" &&
              rentingPeriod[1] !== "" &&
              new Intl.DateTimeFormat("en-US", {
                month: "short",
                day: "numeric",
              }).format(new Date((rentingPeriod[0] - diff) * 1000)) +
                "~" +
                new Intl.DateTimeFormat("en-US", {
                  month: "short",
                  day: "numeric",
                }).format(new Date((rentingPeriod[1] - diff) * 1000))}
          </div>
        </div>
        <div className="flex justify-between">
          <div>Paid</div>
          <div className="flex items-center gap-[10px]">
            <NUSDIcon />
            <div className="text-[#5b1dee]">
              {fundedAmountSafe.toFixed(2)}
            </div>
            <div>USDC</div>
          </div>
        </div>
        <div className="flex justify-between">
          <div>Pending</div>
          <div className="flex items-center gap-[10px]">
            <NUSDIcon />
            <div className="text-[#5b1dee]">
              {new Date((rentingPeriod[1] - diff) * 1000) < currentTime ||
              cancelled
                ? 0
                : approved
                ? cancelled
                  ? fundedAmountSafe.toFixed(2)
                  : fundedAmountSafe.toFixed(2)
                : 0}
            </div>
            <div>USDC</div>
          </div>
        </div>
        <div className="flex justify-between">
          <div>Claimable</div>
          <div className="flex items-center gap-[10px]">
            <NUSDIcon />
            <div className="text-[#5b1dee]">
              {new Date((rentingPeriod[1] - diff) * 1000) < currentTime ||
              cancelled
                ? approved
                  ? cancelled
                    ? fundedAmountSafe.toFixed(2)
                    : fundedAmountSafe.toFixed(2)
                  : 0
                : 0}
            </div>
            <div>USDC</div>
          </div>
        </div>
      </div>
      {new Date((rentingPeriod[0] - diff) * 1000) > currentTime &&
        !cancelled && (
          <div className="w-full grid grid-cols-2 gap-[10px]">
            {!approved && (
              <PurpleButton
                onClick={() => {
                  approveReservation();
                }}
                text="Approve"
              />
            )}
            {
              <div className={approved ? "col-span-2" : ""}>
                <RedButton
                  onClick={() => {
                    rejectReservation();
                  }}
                  text="Cancel"
                />
              </div>
            }
          </div>
        )}
      {new Date((rentingPeriod[0] - diff) * 1000) < currentTime &&
        new Date((rentingPeriod[1] - diff) * 1000) > currentTime && (
          <RedButton
            onClick={() => {
              rejectReservation();
            }}
            text="Cancel Approval"
          />
        )}
      {new Date((rentingPeriod[1] - diff) * 1000) < currentTime &&
        approved &&
        !cancelled &&
        !reviewPlaced && (
          <BlackButton
            onClick={() => {
              setReviewMode();
            }}
            text="Write a Review"
          />
        )}

      {(new Date((rentingPeriod[1] - diff) * 1000) < currentTime ||
        cancelled) &&
        approved && (
          <BlackButton
            onClick={() => finalizeRental()}
            text="Finalize Rental"
          />
        )}
    </div>
  );
};
